import { cn } from '@helpers/cn';
import { EntityIcon } from '@components/core-ui/EntityIcon';
import { PopoverFloating as Popover } from '@components/core-ui/PopoverFloating';
import AccountIcon from '@components/icons/Account.svg';
import ASOneIcon from '@components/icons/ASOneOutline.svg';
import DesktopIcon from '@components/icons/DesktopOutline.svg';
import LogoutIcon from '@components/icons/Logout.svg';
import SupportIcon from '@components/icons/Support.svg';
import { getASOneDownloadLink } from '@helpers/getASOneDownloadLink';
import { applyTheme, getStoredTheme, parseTheme, setTheme as setAppTheme, TTheme } from '@helpers/theme';
import { useAuthContext } from '@providers/AuthProvider';
import { useInitContext } from '@providers/InitProvider';
import { routes } from '@routes';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { AvatarThemeSwitch } from './components/AvatarThemeSwitch';

export const Avatar = () => {
  const navigate = useNavigate();
  const { me } = useAuthContext();
  const { logout } = useInitContext();
  const wrapperRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isDarkTheme, setIsDarkTheme] = useState(() => getStoredTheme() === 'dark');

  useEffect(() => {
    const onThemeStorageUpdate = (event: StorageEvent) => {
      if (event.key === 'theme') {
        const theme = parseTheme(event.newValue);
        applyTheme(theme);
        setIsDarkTheme(theme === 'dark');
      }
    };

    window.addEventListener('storage', onThemeStorageUpdate);

    return () => {
      window.removeEventListener('storage', onThemeStorageUpdate);
    };
  }, []);

  if (!me || !me.name) {
    return null;
  }

  const commonClassName =
    'm-0 px-3 py-2.5 text-[var(--color-text-muted)] font-medium text-sm leading-[150%] text-left align-middle flex items-center gap-2.5 rounded-[6px] cursor-pointer';
  const itemClassName = `${commonClassName} hover:bg-[var(--color-surface-muted)]`;

  const setTheme = (theme: TTheme) => {
    setAppTheme(theme);
    setIsDarkTheme(theme === 'dark');
  };

  const toggleOpen = () => setAnchorEl((current) => (current ? null : wrapperRef.current));

  const isOpen = anchorEl !== null;

  return (
    <>
      <div className="flex justify-center items-center rounded-[5px]">
        <button
          type="button"
          aria-label="Account menu"
          aria-expanded={isOpen}
          onClick={toggleOpen}
          ref={wrapperRef}
          className={cn(
            'cursor-pointer min-h-11 min-w-11 flex items-center justify-center -mx-0.5 relative rounded-[5px] border-2 hover:border-[var(--color-title)]',
            isOpen ? 'border-[var(--color-title)]' : 'border-transparent',
          )}
        >
          <EntityIcon name={`${me.name} ${me.lastname}`} src={me.avatar || undefined} />
        </button>
      </div>

      <Popover
        open={isOpen}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorEl={anchorEl}
        className="rounded-lg max-w-[calc(100vw-2rem)]"
      >
        <ul className="list-none p-1.5 m-0 min-w-[182px]">
          <li onClick={() => navigate(routes.accountPage.pattern)} className={itemClassName}>
            <AccountIcon className="w-5 h-5" />
            My Account
          </li>
          <li className="h-0 border-b border-[var(--color-border)] p-0 m-0 my-0.5" />
          <li
            onClick={() => window?.open('https://support.voicescript.ai/portal/', '_blank')}
            className={itemClassName}
          >
            <SupportIcon className="w-5 h-5" />
            Support
          </li>
          <li className="h-0 border-b border-[var(--color-border)] p-0 m-0 my-0.5" />
          <li onClick={() => navigate(routes.desktopAppHelp.pattern)} className={itemClassName}>
            <DesktopIcon className="w-5 h-5" />
            Desktop App
          </li>
          <li className="h-0 border-b border-[var(--color-border)] p-0 m-0 my-0.5" />
          <li onClick={() => window?.open(getASOneDownloadLink(), '_blank')} className={itemClassName}>
            <ASOneIcon className="w-5 h-5" />
            AutoScriptOne App
          </li>
          <li className="h-0 border-b border-divider p-0 m-0 my-0.5" />
          <li
            className={`${itemClassName} justify-between cursor-default`}
            onClick={() => setTheme(isDarkTheme ? 'light' : 'dark')}
          >
            <span>Dark Mode</span>
            <AvatarThemeSwitch
              checked={isDarkTheme}
              className={`switch${isDarkTheme ? ' is-on' : ''}`}
              onChange={() => setTheme(isDarkTheme ? 'light' : 'dark')}
            />
          </li>
          <li className="h-0 border-b border-divider p-0 m-0 my-0.5" />
          <li
            onClick={() => void logout()}
            className={`${commonClassName} hover:bg-[var(--color-danger)] hover:text-[var(--color-on-surface)]`}
          >
            <LogoutIcon className="w-5 h-5" />
            Logout
          </li>
        </ul>
      </Popover>
    </>
  );
};
